import React, { useState, useEffect } from "react";
import { Card, Tag, Row, Col, Dropdown, Menu, Skeleton } from "antd";
import {
  PlusOutlined,
  MoreOutlined,
  EyeOutlined,
  FormOutlined,
  DeleteOutlined,
  EditOutlined,
} from "@ant-design/icons";
import styled from "styled-components";
import { CustomButton } from "../../physician_page/styled";
import { capitalizeFirstLetter } from "../../../../utils/utils";
import { MdMoreVert } from "react-icons/md";
import journeyService from "../../../../services/journey";
import ViewTemplatesModal from "../modals/ViewTemplatesModal";
import CreateTemplateModal from "../modals/CreateTemplateModal";
import DeleteConfirmationModal from "../../physician_page/component/DeleteConfirmationModal";
import { notify } from "../../../../utils/helper";
import CreateJourneyModal from "../modals/CreateJourneyModal";
import { extractToken } from "../../../../utils/localStorage";
import { globalRoles } from "../../../../utils/roles";

const TemplateCardWrapper = styled(Card)`
  border: 1px solid var(--border-color);
  border-radius: 12px;
  background-color: var(--primary-bg);
  transition: all 0.3s ease;
  height: 100%;

  &:hover {
    box-shadow: 0 4px 12px rgba(138, 62, 244, 0.1);
    border-color: rgba(138, 62, 244, 0.3);
  }

  .ant-card-body {
    padding: 20px;
    display: flex;
    flex-direction: column;
    height: 100%;
  }
  .ant-dropdown {
    z-index: 10;
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  // margin-bottom: 16px;
`;

const CardTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: var(--primary-text-color);
  margin: 0 0 5px 0;
  line-height: 1.4;
`;

const CardDescription = styled.p`
  font-size: 14px;
  color: var(--secondary-text-color);
  margin: 0 0 20px 0;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const InfoLabel = styled.div`
  font-size: 14px;
  color: var(--secondary-text-color);
  margin-bottom: 6px;
`;

const InfoValue = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: var(--primary-text-color);
`;

const PublicTag = styled(Tag)`
  background-color: ${({ templateType }) => templateType === "public" ? "#e0f2fe" : templateType === "private" ? "#eef2ff" : "#d9d9d9"};
  color: ${({ templateType }) => templateType === "public" ? "#0369a1" : templateType === "private" ? "#4338ca" : "#666666"};
  border: none;
  border-radius: 15px;
  padding: 3px 15px;
  font-size: 12px;
  font-weight: 600;
  margin: 0;
`;

const UsageText = styled.span`
  font-size: 14px;
  color: var(--secondary-text-color);
  margin-left: 8px;
`;

const CardFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: auto;
  padding-top: 16px;
  border-top: 1px solid var(--border-color);
`;

const FooterLeft = styled.div`
  display: flex;
  align-items: center;
`;

const UseTemplateButton = styled(CustomButton)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 14px;
  padding: 8px 16px;
`;

const MenuIcon = styled(MdMoreVert)`
  font-size: 30px;
  color: var(--secondary-text-color);
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s;

  &:hover {
    background-color: var(--border-color);
    color: var(--primary-text-color);
  }
`;

export const StyledMenu = styled(Menu)`
  min-width: 150px;
  padding: 4px;
  border-radius: 8px;
  background-color: var(--primary-bg);
  border: 1px solid var(--border-color);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);

  .ant-dropdown-menu-item {
    padding: 5px 10px;
    // margin: 2px 0;
    border-radius: 6px;
    transition: all 0.3s ease;
    color: var(--primary-text-color);

    &:hover {
      background-color: var(--border-color);
      color: color: var(--primary-text-color);
    }

    .menu-item-content {
      display: flex;
      align-items: center;
      gap: 8px;

      .menu-item-icon {
        color: var(--primary-text-color);
        font-size: 16px;
        margin-right: 6px;
      }

      .menu-item-text {
        color: var(--primary-text-color);
        font-size: 14px;
        font-weight: 500;
      }
      
      .menu-danger-icon {
        color: #EF4444;
        font-size: 16px;
        margin-right: 6px;
      }

      .menu-danger-text {
        color: #EF4444;
        font-size: 14px;
        font-weight: 500;
      }
    }

    .ant-switch {
      margin-left: auto;
    }
  }

  .ant-dropdown-menu-item-selected {
    background-color: var(--secondary-bg);
    color: var(--primary-text-color);
  }

  .ant-dropdown-menu-item-active {
    background-color: var(--secondary-bg);
  }
  .ant-dropdown-menu-item-divider {
    background-color: var(--border-color) !important;
  }
`;

const TemplateCard = ({ isCreateTemplateModalOpen, setIsCreateTemplateModalOpen }) => {

  const [templatesData, setTemplatesData] = useState([]);
  const [isTemplateLoading, setIsTemplateLoading] = useState(false);
  const [isDeleteModal, setIsDeleteModal] = useState(false);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [isTemplateEdit, setIsTemplateEdit] = useState(false);
  const [templateData, setTemplateData] = useState(null);
  const [isOpenCreateJourneyModal, setIsOpenCreateJourneyModal] = useState(false);
  const [isCreateJourneyByTemplate, setIsCreateJourneyByTemplate] = useState(false);
  
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(9);
  const [hasMore, setHasMore] = useState(true);
  const [loadMoreLoading, setLoadMoreLoading] = useState(false);

  const user = extractToken();
  const isSuperAdmin = user?.role === globalRoles.SUPERADMIN;

  const fetchTemplates = async (page = 1, isLoadMore = false) => {
    try {
      if (isLoadMore) {
        setLoadMoreLoading(true);
      } else {
        setIsTemplateLoading(true);
      }
      
      const response = await journeyService.getTemplates(pageSize, page);
      const results = response?.data?.results || [];

      if (isLoadMore) {
        setTemplatesData((prev) => [...prev, ...results]);
      } else {
        setTemplatesData(results);
      }
      setHasMore(results.length === pageSize);
      setCurrentPage(page);
    } catch (error) {
      console.error("Error fetching templates:", error);
    } finally {
      setIsTemplateLoading(false);
      setLoadMoreLoading(false);
    }
  };

  const handleLoadMore = () => {
    fetchTemplates(currentPage + 1, true);
  };

  const handleDelete = async () => {
    try {
      setIsTemplateLoading(true);
      const res = await journeyService.deleteTemplate({ ids: [templateData.id] });
      fetchTemplates();
      setIsDeleteModal(false);
      notify("success", "Template Delete Successfully.");
      setIsTemplateLoading(false);
    } catch (err) {
      setIsTemplateLoading(false);
    }
  };

  const handleStatusChange = async (status, template) => {
    try {
   
      setIsTemplateLoading(true);
      const res = await journeyService.changeStatus({ id: template.id, status: status });
      fetchTemplates();
      setIsDeleteModal(false);
      notify("success", "Template status updated successfully.");
      setIsTemplateLoading(false);
    } catch (err) {
      setIsTemplateLoading(false);
    }
  };  

  useEffect(() => {
    fetchTemplates();
  }, []);

  const menu = (template, isEditAllowed, isDeleteAllowed) => (
    <StyledMenu>
      <Menu.Item 
        key="preview" 
        onClick={() => {
          setTemplateData(template);
          setIsPreviewModalOpen(true);
        }}
      >
        <div className="menu-item-content">
          <EyeOutlined className="menu-item-icon" />
          <span className="menu-item-text">Preview</span>
        </div>
      </Menu.Item>
      <Menu.Item
        key="duplicate"
        onClick={() => {
              setIsOpenCreateJourneyModal(true)
setTemplateData(template)
setIsCreateJourneyByTemplate(true)
        }}
      >
        <div className="menu-item-content">
          <PlusOutlined className="menu-item-icon" />
          <span className="menu-item-text">Use Template</span>
        </div>
      </Menu.Item>
      {(isSuperAdmin || isEditAllowed) && (
      <Menu.Item 
        key="edit" 
        onClick={() => {
          setTemplateData(template);
          setIsTemplateEdit(true);
          setIsCreateTemplateModalOpen(true);
        }}
      >
        <div className="menu-item-content">
          <FormOutlined className="menu-item-icon" />
          <span className="menu-item-text">Edit</span>
        </div>
      </Menu.Item>
      )}
      {
        (isDeleteAllowed) && (
          <>
          <Menu.Divider />
      <Menu.Item 
        key="delete" 
        onClick={() => {
          setTemplateData(template);
          setIsDeleteModal(true);
        }}
      >
        <div className="menu-item-content">
          <DeleteOutlined className="menu-danger-icon" />
          <span className="menu-danger-text">Delete</span>
        </div>
      </Menu.Item>
      </>
        )}
    </StyledMenu>
  );

  if (isTemplateLoading) {
    return (
      <Row gutter={[16, 16]}>
        {[1, 2, 3].map((idx) => (
          <Col lg={8} md={8} sm={24} xs={24} key={idx}>
            <TemplateCardWrapper>
              <CardHeader>
                <div style={{ flex: 1, maxWidth: "87%" }}>
                  <Skeleton.Button
                    active
                    size="small"
                    style={{ width: "60%", height: 24, marginBottom: 5 }}
                  />
                </div>
                <Skeleton.Button
                  active
                  size="small"
                  style={{ width: 24, height: 24 }}
                />
              </CardHeader>

              <Skeleton
                active
                paragraph={{ rows: 2 }}
                style={{ marginBottom: 20 }}
              />

              <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
                {[1, 2, 3, 4].map((i) => (
                  <Col xs={24} sm={12} key={i}>
                    <Skeleton active paragraph={{ rows: 1 }} title={false} />
                  </Col>
                ))}
              </Row>

              <CardFooter>
                <FooterLeft>
                  <Skeleton.Button
                    active
                    size="small"
                    style={{ width: 80, height: 24 }}
                  />
                </FooterLeft>
                <Skeleton.Button
                  active
                  size="default"
                  style={{ width: 140, height: 36 }}
                />
              </CardFooter>
            </TemplateCardWrapper>
          </Col>
        ))}
      </Row>
    );
  }

  const statusMenu = (template) => (
    <StyledMenu onClick={({ key }) => handleStatusChange(key, template)}>
      <Menu.Item key="draft">
        <span className="menu-item-text">Draft</span>
      </Menu.Item>
      <Menu.Item key="public">
        <span className="menu-item-text">Public</span>
      </Menu.Item>
      <Menu.Item key="private">
        <span className="menu-item-text">Private</span>
      </Menu.Item>
    </StyledMenu>
  );

  return (
    <>
      {isPreviewModalOpen && (
        <ViewTemplatesModal
          isOpen={isPreviewModalOpen}
          setOpen={setIsPreviewModalOpen}
          templateData={templateData}
          setTemplateData={setTemplateData}
          isEdit={isTemplateEdit}
          setIsEdit={setIsTemplateEdit}
          fetchTemplates={fetchTemplates}
        />
      )}

      {isCreateTemplateModalOpen && (
        <CreateTemplateModal
          isOpen={isCreateTemplateModalOpen}
          setOpen={setIsCreateTemplateModalOpen}
          onSuccess={() => {
            fetchTemplates();
          }}
          templateData={templateData}
          isEdit={isTemplateEdit}
          setIsEdit={setIsTemplateEdit}
        />
      )}

      {isDeleteModal && (
        <DeleteConfirmationModal
          isOpen={isDeleteModal}
          setOpen={setIsDeleteModal}
          title={"Delete Template"}
          onConfiem={handleDelete}
          loading={isTemplateLoading}
        />
      )}

      {isOpenCreateJourneyModal && (
        <CreateJourneyModal
          isOpen={isOpenCreateJourneyModal}
          setOpen={setIsOpenCreateJourneyModal}
          isEdit={isOpenCreateJourneyModal}
          setIsEdit={setIsOpenCreateJourneyModal}
          // journeyId={templateData}
          templateData={templateData}
          isCreateJourneyByTemplate={isCreateJourneyByTemplate}
          // onSuccess={handleEditSuccess}
        />
      )}

<Row gutter={[16, 16]}>
      {templatesData?.map((template) => {
        const isEditAllowed = template?.createdBy?.id === user?.sub;
        
        const userRole = user?.role;
        const templateRole = template?.createdBy?.role;

        const isDeleteAllowed = isSuperAdmin || (
          templateRole !== globalRoles.SUPERADMIN && (
            // Same Role deletion (Siblings)
            (userRole === templateRole) ||
            // Hierarchy 1: Coordinator > Manager
            (userRole === globalRoles.CCM_COORDINATOR && templateRole === globalRoles.CCM_MANAGER) ||
            // Hierarchy 2: Physician > Assistant (Scheduler)
            (userRole === globalRoles.PHYSICIAN && templateRole === globalRoles.ASSISTANT)
          )
        );

        return (
        <Col lg={8} md={12} sm={24} xs={24} key={template.id}>
          <TemplateCardWrapper>
            <CardHeader>
              <div style={{ flex: 1, maxWidth: "87%" }}>
                <CardTitle className="ellipsis">
                  {capitalizeFirstLetter(template.name)}
                </CardTitle>
              </div>
              <Dropdown overlay={menu(template, isEditAllowed, isDeleteAllowed)} trigger={["click"]} getPopupContainer={triggerNode => triggerNode.parentNode}>
                <MenuIcon />
              </Dropdown>
            </CardHeader>

            <CardDescription>
              {capitalizeFirstLetter(template.description)}
            </CardDescription>

            <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
              <Col xs={24} sm={12}>
                <InfoLabel>Specialty</InfoLabel>
                <InfoValue>{template.specialty}</InfoValue>
              </Col>
              <Col xs={24} sm={12}>
                <InfoLabel>Condition</InfoLabel>
                <InfoValue>{template.condition}</InfoValue>
              </Col>
              <Col xs={24} sm={12}>
                <InfoLabel>Created By</InfoLabel>
                <InfoValue>{template?.createdBy?.name || "Unknown"}</InfoValue>
              </Col>
              <Col xs={24} sm={12}>
                <InfoLabel>Steps</InfoLabel>
                <InfoValue>{template.steps.length} videos</InfoValue>
              </Col>
            </Row>

            <CardFooter>
              <FooterLeft>
                {template.type && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <PublicTag templateType={template?.type}>{capitalizeFirstLetter(template?.type)}</PublicTag>
                    {(isSuperAdmin || isEditAllowed) && (
                    <Dropdown overlay={statusMenu(template)} trigger={["click"]} getPopupContainer={triggerNode => triggerNode.parentNode}>
                      <EditOutlined style={{ cursor: 'pointer', color: 'var(--secondary-text-color)' }} />
                    </Dropdown>
                    )}
                  </div>
                )}
                <UsageText>Used {template?.usedCount} times</UsageText>
              </FooterLeft>
              <UseTemplateButton onClick={() => {
               setIsOpenCreateJourneyModal(true)
               setTemplateData(template)
               setIsCreateJourneyByTemplate(true)
              }
               }>
                <PlusOutlined />
                Use Template
              </UseTemplateButton>
            </CardFooter>
          </TemplateCardWrapper>
        </Col>
        )
      })}
      </Row>
      {hasMore && (
        <Row className="mt-25 w-100 d-flex justify-content-center" style={{ padding: '20px 0' }}>
          <CustomButton onClick={handleLoadMore} loading={loadMoreLoading}>
            Load More Templates
          </CustomButton>
        </Row>
      )}
      {!hasMore && templatesData.length > 0 && (
        <Row className="mt-25 w-100 d-flex justify-content-center" style={{ padding: '20px 0' }}>
          <p style={{ color: '#666', fontStyle: 'italic' }}>No more templates to load</p>
        </Row>
      )}
    </>
  );
};

export default TemplateCard;
